import { Box, Grid, Modal, Paper } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import {
  Add,
  Button,
  Chip,
  DatePicker,
  FileDropZone,
  FilePresent,
  PageTitleAndSubTitle,
  TextField,
  TypableDropdown,
  UploadedDocument,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import SvgClose from '@HomaHealth/ui-component-library/lib/_components/atoms/Icons/Close';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { scrollBarStyles } from '../../../core-utils/messages/CommonConstants';
import ReportCreateNewLabelField from '../../atoms/ReportCreateNewLabelField';
import Dropdown from '@HomaHealth/ui-component-library/lib/_AIQ-components/atoms/Dropdown';
import { commonDropDownProps } from '../../atoms/ReportsCohortItem';
import moment from 'moment';
import {
  formatFileSize,
  getAvatarComponent,
} from '../../../core-utils/helper-utils/TaskBoardHelper';
import RiskRegisterConstants from '../../../core-utils/messages/RiskRegisterConstants';
import { useStores } from '../../../stores';
import Info from '@HomaHealth/ui-component-library/lib/_AIQ-components/atoms/Icons/Info';
import accessDashboardConstants from '../../../core-utils/messages/AccessDashboardConstants';
import axios from 'axios';
import properties from '../../../core-utils/properties';
import { getTextWithToolTip } from '../../../core-utils/helper-utils/riskPatientDetailsHelper';
import {
  getMitigationChips,
  getPlannedInfoMessages,
  getStatusChip,
  removeEmptyFields,
} from '../../../core-utils/helper-utils/riskRegisterHelper';
import AutocompleteWithCheckBoxes from '../../atoms/Autocomplete';
import { CheckBox } from '@HomaHealth/ui-component-library';

export interface AddRiskModalProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
   
  handleSubmit: (data: any) => void;
  handleOpenSnackBar: any;
  eg_nid: string;
}

export interface ActivityData {
  start_date: string;
  name: string;
  owner: string[];
  tracker: string;
  activity_outcome: string;
}
export interface NewRiskData {
  title: string;
  description: string;
  date_identified: string;
  impact: any[];
  risk_source: string;
  severity_level: string;
  risk_status: string;
  files: File[];
  activity: ActivityData[];
  planned_quarter: string;
  source_report: string;
  selected_acceptance_reason: string;
  selected_delay_reason: string;
  contract_id: string;
  compare_agreement_id: string;
}

const AddRiskModal: React.FC<AddRiskModalProps> = observer(
  ({ open, setOpen, handleSubmit, eg_nid }) => {
    const useStyles = makeStyles()({
      modal: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      },
      paper: {
        width: '676px',
        height: '760px',
        padding: '24px 28px 40px 32px',
        backgroundColor: patientAccessTheme.palette.status.white,
        border: 'none !important',
        position: 'relative',
      },
      button: {
        padding: '0px',
      },
      scroll: scrollBarStyles,
      flex: {
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        marginRight: '25px',
        width: '100%',
      },
      activity: {
        backgroundColor: patientAccessTheme.palette.grey[100],
        padding: '20px 0 0px 20px',
      },
      infoItem: {
        boxSizing: 'border-box',
        width: '586px',
        height: '100px',
        background: '#E1A9481A',
        border: '0px',
        borderRadius: '8px',
        display: 'flex',
        justifyContent: 'center',
        flexDirection: 'column',
        padding: '20px',
      },
    });
    const { classes } = useStyles();

    //States
    const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
    const [data, setData] = useState<NewRiskData>({
      title: '',
      description: '',
      date_identified: moment().format('MMMM D, YYYY'),
      impact: [],
      risk_source: '',
      severity_level: '',
      risk_status: '',
      files: [],
      activity: [
        {
          start_date: '',
          name: '',
          owner: [],
          tracker: '',
          activity_outcome: '',
        },
      ],
      planned_quarter: '',
      source_report: '',
      selected_acceptance_reason: '',
      selected_delay_reason: '',
      contract_id: '',
      compare_agreement_id: '',
    });
    const [selectedChip, setSelectedChip] = useState<string | null>(null);
    const [createMitigation, setCreateMitigation] = useState<boolean>(false);
    const [riskSourceOptions, setRiskSourceOptions] = useState<any>([]);
    const [riskSourceValues, setRiskSourceValues] = useState<any>([]);
    const [contracts, setContracts] = useState<any>([]);
    const [ownerOptions, setOwnerOptions] = useState<any>([]);
    const [trackerOptions, setTrackerOptions] = useState<any>([]);
    const [trackerValues, setTrackerValues] = useState<any>([]);
    const [acceptanceReasons, setAcceptanceReasons] = useState<any>([]);
    const [delayReasons, setDelayReasons] = useState<any>([]);
     
    const [activityOptions, setActivityOptions] = useState<any>([]);

    //Constants
    const messages = RiskRegisterConstants.MAIN_PAGE;
    const isCommonFieldsFilled =
      data.title &&
      data.date_identified &&
      data.impact &&
      data.risk_source &&
      data.risk_status &&
      data.severity_level;

    const isInProgressFieldsFilled = data.activity.some(
      (item) =>
        item.name && item.start_date && item.owner.length > 0 && item.tracker,
    );

    const isDelayReasonFilled = !!data.selected_delay_reason;
    const isAcceptanceReasonFilled = !!data.selected_acceptance_reason;
    const isActivityOutcomeFilled = data.activity.some(
      (item) => item.name && item.activity_outcome,
    );
    const isPlannedAndCurrentQuarter =
      data.risk_status === 'Planned' && selectedChip === 'Current Quarter';

    const isPlannedAndFutureQuarter =
      data.risk_status === 'Planned' &&
      selectedChip !== 'Current Quarter' &&
      !createMitigation;

    const isPlannedWithCreateMitigation =
      data.risk_status === 'Planned' && createMitigation;

    const isMitigated = data.risk_status === 'Mitigated';
    const isAccepted = data.risk_status === 'Accepted';

    const isAddRiskDisabled = !(
      isCommonFieldsFilled &&
      ((data.risk_status === 'In Progress' && isInProgressFieldsFilled) ||
        (isPlannedAndCurrentQuarter && isInProgressFieldsFilled) ||
        (isPlannedAndFutureQuarter && isDelayReasonFilled) ||
        (isPlannedWithCreateMitigation &&
          isDelayReasonFilled &&
          isInProgressFieldsFilled) ||
        (isMitigated && isActivityOutcomeFilled) ||
        (isAccepted && isAcceptanceReasonFilled) ||
        data.risk_status === 'New')
    );
    const userDetailsStore = useStores().userDetailsStore;
    const {
      plannedInfoTitle,
      plannedInfoSub,
      plannedBackground,
      plannedTextColor,
    } = getPlannedInfoMessages(selectedChip);

    //functions
    const handleClose = () => {
      setOpen(false);
    };

    const handleDateIdentifiedChange = (
       
      date: Date | null,
      value?: any,
    ) => {
      setData((prevData) => ({
        ...prevData,
        date_identified: value,
      }));
    };

    const handleChange = (event: any, fieldName: string) => {
      const value = event.target.value as string;
      setData((prevData) => ({
        ...prevData,
        [fieldName.toLowerCase()]: value,
      }));
    };

    //Apis
    const getAcceptanceReasons = async () => {
      const acceptanceReasons = await axios.post(
        properties.aiqBaseUrl +
          properties.riskRegisterBaseUrl +
          properties.getAcceptanceReason,
        { source_report: data.source_report },
      );
      setAcceptanceReasons(acceptanceReasons.data.data);
    };

    const getDelayReasons = async () => {
      const delayReasons = await axios.post(
        properties.aiqBaseUrl +
          properties.riskRegisterBaseUrl +
          properties.getDelayReason,
        { source_report: data.source_report },
      );
      setDelayReasons(delayReasons.data.data);
    };

    const getRiskSourceOptions = async () => {
      const contracts = await axios.post(
        properties.aiqBaseUrl +
          properties.contractBaseUrl +
          properties.getContracts,
        {
          eg_nid,
          // eg_nid: 'denali_water solutions_1733163850381',
        },
      );
      const allContracts = contracts.data.data.map((item: any) => {
        return {
          name: `${item.health_care_partner_name} - ${item.service_agreement_name}`,
          reports: item.reports,
          contract_id: item.contract_id,
        };
      });
      setContracts(allContracts);
      const accessibleScreens = userDetailsStore.allScreens.riskManagement.map(
        (screen) => screen.name,
      );
      const options = [];
      const values = [];
      if (accessibleScreens.includes('Summary')) {
        options.push('Summary');
        values.push('Summary');
      }
      const individualScreens = accessDashboardConstants.DASHBOARD_CARDS.filter(
        (card: any) => accessibleScreens.includes(card.category),
         
      ).flatMap((card: any) => card.subGroups.map((sub: any) => sub.group));
      options.push(
        ...individualScreens.map((item) => getTextWithToolTip(item, 30)),
        ...allContracts.map((item: any) => getTextWithToolTip(item.name, 30)),
      );
      values.push(
        ...individualScreens,
        ...allContracts.map((item: any) => item.name),
      );
      setRiskSourceValues(values);
      setRiskSourceOptions(options);
      const ownerOptions = contracts.data.data.map((item: any) => {
        return `${item.service_agreement_name} - ${item.health_care_partner_name}`;
      });
      setOwnerOptions(ownerOptions);
    };

    const getTrackersList = async () => {
      const roles = ['ADMIN', 'USER'];
      const requests = roles.map((role) =>
        axios.get(
          `${properties.userManagementBaseUrl}${properties.users}${properties.getByRole}/${role}`,
        ),
      );

      const responses = await Promise.all(requests);

      const allUsers = responses
        .flatMap((res) => res.data)
        .filter((user: any) =>
          user.employerGroups?.some((group: any) => group.egNid === 'D0033'),
        );
      const options = allUsers.map((item: any) =>
        getAvatarComponent(item.email, item.firstName, item.lastName),
      );
      const values = allUsers.map((item: any) => item.email);
      setTrackerOptions(options);
      setTrackerValues(values);
      // setTrackerOptions(['ii', 'jj']);
      // setTrackerValues(['ii', 'jj']);
    };

    // const handleActivityInputChange = async (value: any) => {
    //   try {
    //     const res = await axios.post(
    //       `${properties.aiqBaseUrl}${properties.riskRegisterBaseUrl}${properties.getActivities}`,
    //       {
    //         // eg_nid: 'crane_nxt_1742408999728',
    //         eg_nid,
    //         search: value,
    //         limit: 5,
    //       },
    //     );

    //     if (res.status === 200) {
    //       const mitigationOptions = res.data.data.activities.map(
    //         (activity: any) => {
    //           const name = activity.name;
    //           const status = activity.connected_to?.[0]?.risk_status || '';
    //           return {
    //             label: name,
    //             value: name,
    //             status: status,
    //           };
    //         },
    //       );

    //       setActivityOptions(mitigationOptions);
    //     }
    //   } catch (error) {
    //     handleOpenSnackBar('Something went wrong', 'error', 5000);
    //   }
    // };

    useEffect(() => {
      getRiskSourceOptions();
      getTrackersList();
    }, []);

    useEffect(() => {
      getAcceptanceReasons();
      getDelayReasons();
    }, [data?.risk_source, data.source_report]);

    return (
      <>
        <Modal open={open} onClose={handleClose} className={classes.modal}>
          <Paper className={classes.paper}>
            <Grid
              style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '30px',
              }}
            >
              <PageTitleAndSubTitle
                title={messages.ADD_RISK}
                subTitle={''}
                subTitleStyles={{ width: '0px' }}
              />
              <SvgClose
                style={{ cursor: 'pointer' }}
                color={patientAccessTheme.palette.status.icon}
                onClick={handleClose}
              />
            </Grid>
            <Grid
              className={classes.scroll}
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '15px',
                overflowY: 'scroll',
                height: '570px',
              }}
            >
              <ReportCreateNewLabelField
                label={messages.RISK_TITLE}
                component={
                  <TextField
                    width="575px"
                    height="46px"
                    placeholder={messages.RISK_TITLE}
                    onChange={(event: any) => handleChange(event, 'title')}
                    value={data.title}
                    placeholderStyle={{
                      fontSize: '16px',
                    }}
                    error={!data.title}
                  />
                }
                marginBottom="12px"
              />
              <ReportCreateNewLabelField
                label={messages.DESCRIPTION}
                component={
                  <TextField
                    width="575px"
                    height="46px"
                    placeholder={messages.DESCRIPTION}
                    onChange={(event: any) =>
                      handleChange(event, 'description')
                    }
                    value={data.description}
                    placeholderStyle={{
                      fontSize: '16px',
                    }}
                  />
                }
                marginBottom="12px"
              />
              <ReportCreateNewLabelField
                label={messages.DATE_IDENTIFIED}
                component={
                  <DatePicker
                    width="283px"
                    handleChange={handleDateIdentifiedChange}
                    selectedDate={data.date_identified}
                    minDate={'Jan 01, 2020'}
                    maxDate={moment().add(10, 'years')}
                    format={'MMMM d, yyyy'}
                    placeholder={messages.DATE_FORMAT}
                    error={!data.date_identified}
                  />
                }
              />
              <ReportCreateNewLabelField
                label={messages.IMPACT_TYPE}
                component={
                  <AutocompleteWithCheckBoxes
                    options={messages.IMPACT_VALUES}
                    defaultValue={data.impact}
                    handleChange={(_, value) =>
                      setData((prev) => ({ ...prev, impact: value }))
                    }
                    getOptionLabel={(option: any) => option}
                    placeholder={messages.IMPACT_TYPE}
                    width="283px"
                    renderOption={(option: any, { selected }: any) => {
                      const index = messages.IMPACT_VALUES.indexOf(option);
                      return (
                        <Box style={{ display: 'flex', alignItems: 'center' }}>
                          <CheckBox
                            checked={selected}
                            id=""
                            name=""
                            color="primary"
                          />
                          {messages.IMPACT_OPTIONS[index]}
                        </Box>
                      );
                    }}
                    noOptionsText="No Impact Type Found"
                  />
                }
                marginBottom="12px"
              />
              <ReportCreateNewLabelField
                label={messages.RISK_SOURCE}
                component={
                  <Dropdown
                    label={messages.RISK_SOURCE}
                    dropDownOptions={riskSourceOptions}
                    dropDownValues={riskSourceValues}
                    value={data.risk_source}
                    setValue={(value: string) =>
                      setData((prevData) => ({
                        ...prevData,
                        risk_source: value,
                      }))
                    }
                    selectStyles={{
                      ...commonDropDownProps.selectStyles,
                      width: '283px',
                    }}
                    labelTop="10px"
                    onChange={(event: any) => {
                      const contract_id =
                        contracts?.find(
                          (item: any) => item.name === event.target.value,
                        )?.contract_id ?? '';
                      setData((prevData) => ({
                        ...prevData,
                        risk_source: event.target.value,
                        contract_id: contract_id,
                      }));
                    }}
                    error={!data.risk_source}
                  />
                }
              />
              {contracts.find(
                (item: any) => item.name === data.risk_source,
              ) && (
                <ReportCreateNewLabelField
                  label={messages.SOURCE_REPORT}
                  component={
                    <Dropdown
                      label={messages.SOURCE_REPORT}
                      dropDownOptions={
                        contracts.find(
                          (item: any) => item.name === data.risk_source,
                        )?.reports || []
                      }
                      dropDownValues={
                        contracts.find(
                          (item: any) => item.name === data.risk_source,
                        )?.reports || []
                      }
                      value={data.source_report}
                      setValue={(value: string) =>
                        setData((prevData) => ({
                          ...prevData,
                          source_report: value,
                        }))
                      }
                      selectStyles={{
                        ...commonDropDownProps.selectStyles,
                        width: '283px',
                      }}
                      labelTop="10px"
                      onChange={(event: any) =>
                        handleChange(event, 'source_report')
                      }
                      error={!data.source_report}
                    />
                  }
                />
              )}
              <ReportCreateNewLabelField
                label={messages.SEVERITY_LEVEL}
                component={
                  <Dropdown
                    label={messages.SEVERITY_LEVEL}
                    dropDownOptions={messages.SEVERITY_LEVEL_OPTIONS}
                    dropDownValues={messages.SEVERITY_LEVEL_VALUES}
                    value={data.severity_level}
                    setValue={(value: string) =>
                      setData((prevData) => ({
                        ...prevData,
                        severity_level: value,
                      }))
                    }
                    selectStyles={{
                      ...commonDropDownProps.selectStyles,
                      width: '283px',
                    }}
                    labelTop="10px"
                    onChange={(event: any) =>
                      handleChange(event, 'severity_level')
                    }
                    error={!data.severity_level}
                  />
                }
              />
              <ReportCreateNewLabelField
                label={messages.RISK_STATUS}
                component={
                  <Dropdown
                    label={messages.RISK_STATUS}
                    dropDownOptions={messages.STATUS_OPTIONS}
                    dropDownValues={messages.STATUS_VALUES}
                    value={data.risk_status}
                    setValue={(value: string) =>
                      setData((prevData) => ({
                        ...prevData,
                        risk_status: value,
                      }))
                    }
                    selectStyles={{
                      ...commonDropDownProps.selectStyles,
                      width: '283px',
                    }}
                    labelTop="10px"
                    onChange={(event: any) =>
                      handleChange(event, 'risk_status')
                    }
                    error={!data.risk_status}
                  />
                }
              />
              {data.risk_status === 'Planned' && (
                <Grid className={classes.flex}>
                  <Typography
                    fontSize="12px"
                    lineHeight="22px"
                    textColor={patientAccessTheme.palette.text.highemphasis}
                  >
                    {messages.PLAN_MITIGATION}
                  </Typography>
                  <Grid
                    className={classes.flex}
                    style={{ flexDirection: 'row' }}
                  >
                    {getMitigationChips().map((item: string) => {
                      const isSelected = selectedChip === item;
                      const textColor = isSelected
                        ? patientAccessTheme.palette.primary[500]
                        : patientAccessTheme.palette.text.highemphasis;

                      return (
                        <Chip
                          key={item}
                          label={
                            <Typography
                              fontSize="12px"
                              fontType="medium"
                              lineHeight="21.24px"
                              textColor={textColor}
                            >
                              {item}
                            </Typography>
                          }
                          backgroundColor={
                            isSelected
                              ? patientAccessTheme.palette.primary[100]
                              : patientAccessTheme.palette.status.white
                          }
                          border={
                            isSelected
                              ? `1px solid ${patientAccessTheme.palette.primary[500]}`
                              : `1px solid ${patientAccessTheme.palette.grey[300]}`
                          }
                          onClick={() => {
                            setSelectedChip(item);
                            setData((prevData) => ({
                              ...prevData,
                              planned_quarter: item,
                            }));
                          }}
                        />
                      );
                    })}
                  </Grid>
                  {selectedChip !== null && (
                    <>
                      <Grid
                        className={classes.infoItem}
                        style={{ backgroundColor: plannedBackground }}
                      >
                        <Grid style={{ display: 'flex', alignItems: 'center' }}>
                          <Info
                            height={'16px'}
                            width="16px"
                            style={{ marginRight: '8px' }}
                            stroke={plannedTextColor}
                          />
                          <Typography
                            fontSize="14px"
                            lineHeight="177%"
                            fontType="semiBold"
                            textColor={plannedTextColor}
                          >
                            {plannedInfoTitle}
                          </Typography>
                        </Grid>
                        <Typography
                          fontSize="14px"
                          lineHeight="177%"
                          textColor={
                            patientAccessTheme.palette.text.highemphasis
                          }
                        >
                          {plannedInfoSub}
                        </Typography>
                      </Grid>
                      {selectedChip !== 'Current Quarter' && (
                        <>
                          <ReportCreateNewLabelField
                            label={messages.DELAY_REASON}
                            component={
                              <Dropdown
                                label={messages.DELAY_REASON}
                                dropDownOptions={delayReasons.map((acc: any) =>
                                  getTextWithToolTip(acc.reason, 90),
                                )}
                                dropDownValues={delayReasons.map(
                                  (acc: any) => acc.reason,
                                )}
                                value={data.selected_delay_reason}
                                setValue={(value: string) =>
                                  setData((prevData) => ({
                                    ...prevData,
                                    selected_delay_reason: value,
                                  }))
                                }
                                selectStyles={{
                                  ...commonDropDownProps.selectStyles,
                                  width: '586px',
                                }}
                                labelTop="10px"
                                onChange={(event: any) =>
                                  handleChange(event, 'selected_delay_reason')
                                }
                                error={!data.selected_delay_reason}
                              />
                            }
                          />
                          <Grid
                            style={{ display: 'flex', alignItems: 'center' }}
                          >
                            <CheckBox
                              checked={createMitigation}
                              id=""
                              name=""
                              color="primary"
                              handleChange={() =>
                                setCreateMitigation(!createMitigation)
                              }
                            />
                            <Typography
                              fontSize="12px"
                              lineHeight="22px"
                              textColor={
                                patientAccessTheme.palette.text.highemphasis
                              }
                            >
                              {messages.FUTURE_MITIGATION}
                            </Typography>
                          </Grid>
                        </>
                      )}
                    </>
                  )}
                </Grid>
              )}
              {data.risk_status === 'Accepted' && (
                <>
                  <Grid
                    className={classes.infoItem}
                    style={{ background: patientAccessTheme.palette.grey[100] }}
                  >
                    <Grid style={{ display: 'flex', alignItems: 'center' }}>
                      <Info
                        height={'16px'}
                        width="16px"
                        style={{ marginRight: '8px' }}
                      />
                      <Typography
                        fontSize="14px"
                        lineHeight="177%"
                        fontType="semiBold"
                        textColor={patientAccessTheme.palette.text.highemphasis}
                      >
                        {messages.ACCEPTED_INFO_TITLE}
                      </Typography>
                    </Grid>
                    <Typography
                      fontSize="14px"
                      lineHeight="177%"
                      textColor={patientAccessTheme.palette.text.highemphasis}
                    >
                      {messages.ACCEPTANCE_INFO_SUB}
                    </Typography>
                  </Grid>
                  <ReportCreateNewLabelField
                    label={messages.ACCEPTANCE_REASON}
                    component={
                      <Dropdown
                        label={messages.ACCEPTANCE_REASON}
                        dropDownOptions={acceptanceReasons.map((acc: any) =>
                          getTextWithToolTip(acc.reason, 90),
                        )}
                        dropDownValues={acceptanceReasons.map(
                          (acc: any) => acc.reason,
                        )}
                        value={data.selected_acceptance_reason}
                        setValue={(value: string) =>
                          setData((prevData) => ({
                            ...prevData,
                            selected_acceptance_reason: value,
                          }))
                        }
                        selectStyles={{
                          ...commonDropDownProps.selectStyles,
                          width: '586px',
                        }}
                        labelTop="10px"
                        onChange={(event: any) =>
                          handleChange(event, 'selected_acceptance_reason')
                        }
                        error={!data.selected_acceptance_reason}
                      />
                    }
                  />
                </>
              )}
              {data.risk_status === 'Mitigated' &&
                data.activity.map((activityItem, index) => (
                  <div key={index}>
                    <ReportCreateNewLabelField
                      label={messages.MITIGATION_ACTIVITY}
                      component={
                        <TypableDropdown
                          dropDownOptions={activityOptions.map((opt: any) =>
                            getTextWithToolTip(opt.label, 75),
                          )}
                          dropDownValues={activityOptions.map(
                            (opt: any) => opt.value,
                          )}
                          placeholder={messages.MITIGATION_ACTIVITY}
                          value={activityItem.name}
                          handleChange={(event: any) => {
                            const newActivities = [...data.activity];
                            newActivities[index].name = event.target.value;
                            setData((prev) => ({
                              ...prev,
                              activity: newActivities,
                            }));
                          }}
                          handleInputChange={(value) => {
                            const newActivities = [...data.activity];
                            newActivities[index].name = value;
                            setData((prev) => ({
                              ...prev,
                              activity: newActivities,
                            }));
                          }}
                          // onInternalInputChange={(value) => {
                          //   handleActivityInputChange(value);
                          // }}
                          textFieldPlaceholder="Enter Activity here (or) select from below"
                          height="36px"
                          width="548px"
                          error={!activityItem.name}
                        />
                      }
                      marginBottom="12px"
                    />

                    <ReportCreateNewLabelField
                      label={messages.OUTCOME}
                      component={
                        <TypableDropdown
                          dropDownOptions={[]}
                          dropDownValues={[]}
                          placeholder={messages.OUTCOME}
                          value={activityItem.activity_outcome}
                          handleChange={(event: any) => {
                            const value = event.target.value;
                            const updated = [...data.activity];
                            updated[index].activity_outcome = value;
                            setData((prev) => ({
                              ...prev,
                              activity: updated,
                            }));
                          }}
                          handleInputChange={(value) => {
                            const updated = [...data.activity];
                            updated[index].activity_outcome = value;
                            setData((prev) => ({
                              ...prev,
                              activities: updated,
                            }));
                          }}
                          textFieldPlaceholder="Enter outcome of activity here (or) select from below"
                          height="36px"
                          width="586px"
                          error={!activityItem.activity_outcome}
                        />
                      }
                      marginBottom="12px"
                    />
                  </div>
                ))}
              {(data.risk_status === 'In Progress' ||
                selectedChip === 'Current Quarter' ||
                createMitigation) &&
                data.activity.map((act, index) => (
                  <Grid
                    key={index}
                    className={`${classes.flex} ${classes.activity}`}
                    style={{ marginBottom: '20px' }}
                  >
                    <ReportCreateNewLabelField
                      label={messages.START_DATE}
                      component={
                        <DatePicker
                          width="283px"
                          handleChange={(value) => {
                            const newActivities = [...data.activity];
                            newActivities[index].start_date = moment(
                              value,
                            ).format('MMMM D, YYYY');
                            setData((prev) => ({
                              ...prev,
                              activity: newActivities,
                            }));
                          }}
                          selectedDate={act.start_date}
                          minDate={'Jan 01, 2020'}
                          maxDate={moment().add(1, 'years')}
                          format={'MMMM d, yyyy'}
                          placeholder={messages.DATE_FORMAT}
                          error={!act.start_date}
                        />
                      }
                    />

                    <ReportCreateNewLabelField
                      label={messages.MITIGATION_ACTIVITY}
                      component={
                        <TypableDropdown
                          dropDownOptions={activityOptions.map((opt: any) => (
                            <div
                              style={{
                                display: 'flex',
                                gap: '20px',
                                width: '500px',
                              }}
                            >
                              {getTextWithToolTip(opt.label, 35)}
                              {getStatusChip(opt.status)}
                              <Typography
                                fontSize="12px"
                                lineHeight="22px"
                                textColor={
                                  patientAccessTheme.palette.text.mediumemphasis
                                }
                              >
                                {'Activity Available'}
                              </Typography>
                            </div>
                          ))}
                          dropDownValues={activityOptions.map(
                            (opt: any) => opt.value,
                          )}
                          placeholder={messages.MITIGATION_ACTIVITY}
                          value={act.name}
                          handleChange={(event: any) => {
                            const newActivities = [...data.activity];
                            newActivities[index].name = event.target.value;
                            setData((prev) => ({
                              ...prev,
                              activity: newActivities,
                            }));
                          }}
                          handleInputChange={(value) => {
                            const newActivities = [...data.activity];
                            newActivities[index].name = value;
                            setData((prev) => ({
                              ...prev,
                              activity: newActivities,
                            }));
                          }}
                          // onInternalInputChange={(value) => {
                          //   handleActivityInputChange(value);
                          // }}
                          textFieldPlaceholder="Enter Activity here (or) select from below"
                          height="36px"
                          width="548px"
                          error={!act.name}
                        />
                      }
                      marginBottom="12px"
                    />

                    <Grid
                      className={classes.flex}
                      style={{ flexDirection: 'row', gap: '15px' }}
                    >
                      <ReportCreateNewLabelField
                        label={messages.OWNER}
                        component={
                          <AutocompleteWithCheckBoxes
                            options={ownerOptions}
                            defaultValue={act.owner}
                            handleChange={(_, value) => {
                              const newActivities = [...data.activity];
                              newActivities[index].owner = value;
                              setData((prev) => ({
                                ...prev,
                                activity: newActivities,
                              }));
                            }}
                            getOptionLabel={(option: any) => option}
                            placeholder={messages.OWNER}
                            width="283px"
                            renderOption={(option: any, { selected }: any) => {
                              return (
                                <Box
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                  }}
                                >
                                  <CheckBox
                                    checked={selected}
                                    id=""
                                    name=""
                                    color="primary"
                                  />
                                  {getTextWithToolTip(option, 30)}
                                </Box>
                              );
                            }}
                            noOptionsText="No Owner Found"
                          />
                        }
                        marginBottom="12px"
                      />

                      <ReportCreateNewLabelField
                        label={messages.TRACKER}
                        component={
                          <Dropdown
                            label={messages.TRACKER}
                            dropDownOptions={trackerOptions}
                            dropDownValues={trackerValues}
                            value={act.tracker}
                            setValue={(value: string) => {
                              const newActivities = [...data.activity];
                              newActivities[index].tracker = value;
                              setData((prev) => ({
                                ...prev,
                                activity: newActivities,
                              }));
                            }}
                            selectStyles={{
                              ...commonDropDownProps.selectStyles,
                              width: '266.5px',
                            }}
                            labelTop="10px"
                            onChange={(event: any) => {
                              const newActivities = [...data.activity];
                              newActivities[index].tracker = event.target.value;
                              setData((prev) => ({
                                ...prev,
                                activity: newActivities,
                              }));
                            }}
                            error={!act.tracker}
                          />
                        }
                      />
                    </Grid>
                  </Grid>
                ))}
              {(data.risk_status === 'Mitigated' ||
                data.risk_status === 'In Progress' ||
                selectedChip === 'Current Quarter' ||
                createMitigation) && (
                <Button
                  label={
                    <Typography
                      textColor={patientAccessTheme.palette.primary[500]}
                      fontSize="12px"
                      lineHeight="12px"
                      fontType={'medium'}
                    >
                      {messages.ADD_MITIGATION}
                    </Typography>
                  }
                  variant="outlined"
                  height="36px"
                  width="180px"
                  styleProps={{ marginRight: '25px' }}
                  borderRadius="4px"
                  className={classes.button}
                  startIcon={
                    <Add
                      stroke={patientAccessTheme.palette.primary[500]}
                      fill={patientAccessTheme.palette.primary[500]}
                    />
                  }
                  onClick={() => {
                    setData((prev) => ({
                      ...prev,
                      activity: [
                        ...prev.activity,
                        {
                          name: '',
                          activity_outcome: '',
                          start_date: '',
                          owner: [],
                          tracker: '',
                        },
                      ],
                    }));
                  }}
                />
              )}
              <Grid className={classes.flex}>
                <Typography
                  fontSize="12px"
                  lineHeight="22px"
                  textColor={patientAccessTheme.palette.text.highemphasis}
                >
                  {'Attachments'}
                </Typography>
                <FileDropZone
                  filesSupported={['.pdf', '.doc', '.docx']}
                  uploadedFiles={uploadedFiles}
                  setUploadedFiles={setUploadedFiles}
                  isMultiple={true}
                />
                {uploadedFiles?.map((file: File, index: number) => (
                  <UploadedDocument
                    key={index}
                    name={file.name}
                    size={formatFileSize(file.size)}
                    startIcon={
                      <FilePresent
                        stroke={patientAccessTheme.palette.primary[500]}
                        fill={patientAccessTheme.palette.primary[500]}
                      />
                    }
                    handleDelete={() => {
                      const updatedFiles = [...uploadedFiles];
                      updatedFiles.splice(index, 1);
                      setUploadedFiles(updatedFiles);
                    }}
                  />
                ))}
              </Grid>
            </Grid>

            <Grid
              style={{
                display: 'flex',
                justifyContent: 'right',
                margin: '0px 40px 40px 0px',
                position: 'absolute',
                right: '0px',
                bottom: '0px',
              }}
            >
              <Button
                label={
                  <Typography
                    textColor={patientAccessTheme.palette.primary[500]}
                    fontSize="12px"
                    lineHeight="12px"
                    fontType={'medium'}
                  >
                    {messages.CANCEL}
                  </Typography>
                }
                variant="outlined"
                height="36px"
                width="180px"
                styleProps={{ marginRight: '25px' }}
                borderRadius="4px"
                className={classes.button}
                onClick={() => {
                  setOpen(false);
                }}
              />
              <Button
                label={
                  <Typography
                    textColor={patientAccessTheme.palette.status.white}
                    fontSize="12px"
                    lineHeight="12px"
                    fontType={'medium'}
                  >
                    {messages.ADD_RISK}
                  </Typography>
                }
                variant="contained"
                color="primary"
                height="36px"
                width="180px"
                borderRadius="4px"
                className={classes.button}
                onClick={() => {
                  const cleanedData = removeEmptyFields(data);
                  handleSubmit({
                    ...cleanedData,
                    files: uploadedFiles,
                    ...(cleanedData.planned_quarter === 'Current Quarter'
                      ? { risk_status: 'In Progress' }
                      : {}),
                  });
                  setOpen(false);
                }}
                disabled={isAddRiskDisabled}
              />
            </Grid>
          </Paper>
        </Modal>
      </>
    );
  },
);

export default AddRiskModal;
